import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import {
  useOrderMirrorSyncableCompaniesQuery,
  useOrderMirrorSyncRequestsQuery,
  useRequestOrderMirrorSyncMutation,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";

const styles = {
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  } satisfies SxProps<Theme>,
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    maxWidth: 640,
    border: 1,
    borderColor: "divider",
    borderRadius: 1,
    p: 2,
  } satisfies SxProps<Theme>,
  dateRow: {
    display: "flex",
    gap: 2,
  } satisfies SxProps<Theme>,
};

/** Maps a sync status to a chip color for the recent-requests table. */
const STATUS_COLOR: Record<string, "default" | "info" | "success" | "error"> = {
  PENDING: "default",
  PROCESSING: "info",
  COMPLETED: "success",
  FAILED: "error",
};

/**
 * Non-production panel: request a replay of historical production orders
 * into this environment (all companies or an explicit selection,
 * over a required datetime window) and watch recent requests complete.
 * Delivery is best-effort; re-running the same window is safe — already
 * mirrored orders fail the unique constraint instead of duplicating.
 */
const MirrorSyncPanel = () => {
  const { data: companiesData, error: companiesError } =
    useOrderMirrorSyncableCompaniesQuery();
  const {
    data: requestsData,
    refetch: refetchRequests,
    error: requestsError,
  } = useOrderMirrorSyncRequestsQuery({ fetchPolicy: "network-only" });
  const [requestOrderMirrorSync, { loading: submitting }] =
    useRequestOrderMirrorSyncMutation();

  const [scope, setScope] = useState<"ALL" | "COMPANIES">("ALL");
  const [selectedCompanyUuids, setSelectedCompanyUuids] = useState<string[]>(
    [],
  );
  const [fromDateTime, setFromDateTime] = useState<string>(
    dayjs().subtract(1, "day").format("YYYY-MM-DDTHH:mm"),
  );
  const [toDateTime, setToDateTime] = useState<string>(
    dayjs().format("YYYY-MM-DDTHH:mm"),
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [acceptedRequestUuid, setAcceptedRequestUuid] = useState<string | null>(
    null,
  );

  const companies = useMemo(
    () => companiesData?.orderMirrorSyncableCompanies ?? [],
    [companiesData],
  );
  const requests = useMemo(
    () => requestsData?.orderMirrorSyncRequests ?? [],
    [requestsData],
  );

  const submit = useCallback(async () => {
    setSubmitError(null);
    setAcceptedRequestUuid(null);
    if (scope === "COMPANIES" && selectedCompanyUuids.length === 0) {
      setSubmitError("Select at least one company.");
      return;
    }
    try {
      const result = await requestOrderMirrorSync({
        variables: {
          input: {
            scope,
            companyUuids: scope === "COMPANIES" ? selectedCompanyUuids : null,
            fromDateTime: dayjs(fromDateTime).toISOString(),
            toDateTime: dayjs(toDateTime).toISOString(),
          },
        },
      });
      throwOnGraphqlErrors(result);
      setAcceptedRequestUuid(
        result.data?.requestOrderMirrorSync.requestUuid ?? null,
      );
      await refetchRequests();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Request failed");
    }
  }, [
    fromDateTime,
    refetchRequests,
    requestOrderMirrorSync,
    scope,
    selectedCompanyUuids,
    toDateTime,
  ]);

  return (
    <Box sx={styles.panel}>
      <Typography variant="body1">
        Request a copy of historical production orders into this environment.
        Delivery is best-effort; re-run the same window if something was missed.
      </Typography>
      {companiesError !== undefined && (
        <Alert severity="error">{companiesError.message}</Alert>
      )}
      <Box sx={styles.form}>
        <RadioGroup
          row
          value={scope}
          onChange={(event) => {
            setScope(event.target.value as "ALL" | "COMPANIES");
          }}
        >
          <FormControlLabel
            control={<Radio />}
            label="All companies"
            value="ALL"
          />
          <FormControlLabel
            control={<Radio />}
            label="Select companies"
            value="COMPANIES"
          />
        </RadioGroup>
        {scope === "COMPANIES" && (
          <Autocomplete
            multiple
            getOptionLabel={(option) => option.name}
            options={companies}
            renderInput={(params) => (
              <TextField {...params} label="Companies" />
            )}
            size="small"
            value={companies.filter((c) =>
              selectedCompanyUuids.includes(c.companyUuid),
            )}
            onChange={(_, value) => {
              setSelectedCompanyUuids(value.map((c) => c.companyUuid));
            }}
          />
        )}
        <Box sx={styles.dateRow}>
          <TextField
            helperText="Your local time"
            label="From"
            size="small"
            type="datetime-local"
            value={fromDateTime}
            onChange={(event) => {
              setFromDateTime(event.target.value);
            }}
          />
          <TextField
            helperText="Your local time"
            label="To"
            size="small"
            type="datetime-local"
            value={toDateTime}
            onChange={(event) => {
              setToDateTime(event.target.value);
            }}
          />
        </Box>
        {submitError !== null && <Alert severity="error">{submitError}</Alert>}
        {acceptedRequestUuid !== null && (
          <Alert severity="success">
            Sync request accepted: {acceptedRequestUuid}
          </Alert>
        )}
        <Box>
          <Button
            disabled={submitting}
            variant="contained"
            onClick={() => {
              void submit();
            }}
          >
            Request sync
          </Button>
        </Box>
      </Box>
      <Stack alignItems="center" direction="row" spacing={1}>
        <Typography variant="h6">Recent requests</Typography>
        <Button
          size="small"
          onClick={() => {
            void refetchRequests();
          }}
        >
          Refresh
        </Button>
      </Stack>
      {requestsError !== undefined && (
        <Alert severity="error">{requestsError.message}</Alert>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Requested</TableCell>
            <TableCell>Scope</TableCell>
            <TableCell>Window</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Sent / Created / Existed</TableCell>
            <TableCell>Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((request) => (
            <TableRow key={request.uuid}>
              <TableCell>
                {dayjs(request.createdAt as string).format("YYYY-MM-DD HH:mm")}
              </TableCell>
              <TableCell>
                {request.scope === "ALL"
                  ? "All companies"
                  : `${request.companyUuids.length} companies`}
              </TableCell>
              <TableCell>
                {dayjs(request.fromDateTime as string).format(
                  "YYYY-MM-DD HH:mm",
                )}
                {" -> "}
                {dayjs(request.toDateTime as string).format("YYYY-MM-DD HH:mm")}
              </TableCell>
              <TableCell>
                <Chip
                  color={STATUS_COLOR[request.status] ?? "default"}
                  label={request.status}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {request.matchedCount === null &&
                request.createdCount === null ? (
                  ""
                ) : (
                  <Typography
                    color={
                      (request.matchedCount ?? 0) -
                        (request.createdCount ?? 0) -
                        (request.existedCount ?? 0) >
                      0
                        ? "error"
                        : "text.primary"
                    }
                    component="span"
                    variant="body2"
                  >
                    {request.matchedCount ?? 0} sent /{" "}
                    {request.createdCount ?? 0} created /{" "}
                    {request.existedCount ?? 0} existed
                  </Typography>
                )}
              </TableCell>
              <TableCell>{request.error ?? ""}</TableCell>
            </TableRow>
          ))}
          {requests.length === 0 && (
            <TableRow>
              <TableCell colSpan={6}>No sync requests yet.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default MirrorSyncPanel;
